import { useEffect, useState, useCallback } from "react";
import { UserContext } from "../Definitions/UserContext";
import { AsyncGetLocation, NearbyLocationData } from "../Utilities/BusinessData";
import { AsyncAjaxGet } from "../Utilities/AsyncAjaxService";
import { GetNickNameParams } from "./RenderBusinessOwnerName";
import { NoRecordsBanner } from "../OopsBanner";
import { GetAliasLink, GetPageURLFromPageId } from "../Utilities/LinkUtils";
import React from "react";
import "./RenderNearbyLocation.scss";

// API endpoint for hero image by nickname (always includes NickName param)
const HERO_NICKNAME_API = "merchants/mysite/nickname";

const distanceURL = "merchants/mysite/distance";

// NearbyLocationData type representing a business location from the distance API


// Props interface for the shared component
export interface RenderNearbyLocationsProps {
    userContext: UserContext;
    data?: NearbyLocationData[] | { isStenciled: boolean }; // Pre-fetched data from GenericBusinessControl
    showImage?: boolean;
    showDistance?: boolean;
    isEditor?: boolean;
    linkPage?: string; // Page URL to navigate to (empty = home page)
    linkTarget?: string; // "_blank" for new tab, "" for same window
}

// Default configuration values
const DEFAULT_SHOW_IMAGE = true;
const DEFAULT_SHOW_DISTANCE = true;

// Squared distance calculation for efficient sorting (no sqrt needed)
export function squaredDistance(
    currentLat: number,
    currentLon: number,
    targetLat: number,
    targetLon: number
): number {
    const dx = targetLat - currentLat;
    const dy = targetLon - currentLon;
    return dx * dx + dy * dy;
}

// Get the current location's nickname from DOM or URL
// export function getNickname(): string {

//     // Try to get from DOM element (server-rendered)
//     const nicknameDom = document.getElementById("vwbnickname");
//     if (nicknameDom) {
//         const value = (nicknameDom as HTMLInputElement).value;
//         if (value && value.length > 0) {
//             return value;
//         }
//     }

//     // Fallback to search query '?locationName='
//     return GetNickNameParams();
// }

// Find current location by matching alias or businessName
// export function findCurrentLocation(
//     locations: NearbyLocationData[],
//     nickname: string
// ): NearbyLocationData | undefined {
//     if (!nickname || nickname.length === 0) {
//         return undefined;
//     }
//     return locations.find(
//         (l) => l.alias === nickname || l.businessName === nickname
//     );
// }

/**
 * Format distance using Apple Maps-style approach
 * - Under 0.5 mi/km: show one decimal (e.g., "0.3 mi")
 * - 0.5 mi/km and above: round to nearest integer (e.g., "3 mi")
 */
function formatDistance(distance: number): string {
    if (distance < 0.5) {
        let str = distance.toFixed(1);
        if(str=="0.0"){
            return "0.1";
        }
        return str;
    }
    return Math.round(distance).toString();
}

// Loading skeleton component
function LoadingSkeleton() {
    return (
        <div className="v-nearby-locations">
            {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="v-nearby-card v-nearby-skeleton" />
            ))}
        </div>
    );
}

// Location card component with hero image fetching
interface NearbyLocationCardProps {
    location: NearbyLocationData;
    showImage: boolean;
    showDistance: boolean;
    userContext: UserContext;
    linkPage: string;
    linkTarget: string;
    isEditorMode: boolean;
}

function NearbyLocationCard({
    location,
    showImage,
    showDistance,
    userContext,
    linkPage,
    linkTarget,
    isEditorMode,
}: NearbyLocationCardProps) {




    const distanceUnit = location.countryId === 1 ? "mi" : "km";
    const displayName = location.locationNickName;

    // Build the link URL
    const href = GetAliasLink(
        location.urlSlug,
        linkPage,
        userContext,
        false
    );

    // Handle click - set localStorage for multi-location navigation
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (isEditorMode) {
            e.preventDefault();
            return;
        }
        // Set location data in localStorage before navigation
        //SetLocationStorage(location.vagaroURL, location.encBID, location.bidHash);
    };



    return (
        <a
            href={href}
            target={linkTarget || "_self"}
            rel={linkTarget === "_blank" ? "noopener noreferrer" : undefined}
            className="v-nearby-card"
            onClick={handleClick}
            onPointerDown={(e) => e.stopPropagation()}
        >
            {showImage && (
                <img
                    loading="lazy"
                    src={ location?.imageUrls[0]??location.image}
                    alt={displayName}
                    className="v-nearby-card-image"

                />
            )}
            <div className="v-nearby-card-content">
                <h2 className="v-nearby-card-title">{displayName.replaceAll("&amp;", "&")}</h2>
                <div className="v-nearby-card-address">
                    <p>{location.address + (location.streetNo.length>0 ? " "+location.streetNo : "")}</p>
                    <p>
                        {location.city}, {location.stateCode} {location.zip}
                    </p>
                </div>
                {showDistance && location.distance > 0 && (
                    <div className="v-nearby-card-distance">
                        {formatDistance(location.distance)} {distanceUnit}
                    </div>
                )}
            </div>
        </a>
    );
}

// Main component
export default function RenderNearbyLocations({
    userContext,
    data,
    showImage = DEFAULT_SHOW_IMAGE,
    showDistance = DEFAULT_SHOW_DISTANCE,
    isEditor,
    linkPage = "",
    linkTarget = "",
}: RenderNearbyLocationsProps) {
    const [nearbyLocations, setNearbyLocations] = useState<NearbyLocationData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Determine if we're in editor mode
    const editorMode =
        isEditor !== undefined
            ? isEditor
            : (window as Window & { isEditor?: boolean }).isEditor === true;

    // Check if pre-fetched data is provided
    const hasPreFetchedData = data && Array.isArray(data);
    const isStenciled = data && !Array.isArray(data) && (data as { isStenciled: boolean }).isStenciled;

    // Fetch and process locations
    // const fetchLocations = useCallback(() => {
    //     setIsLoading(true);
    //     setError(null);

    //     AsyncGetLocation(
    //         userContext,
    //         0,
    //         0,
    //         "",
    //         distanceURL,
    //         true,
    //         (data: NearbyLocationData[]) => {
    //             if (!data || data.length === 0) {
    //                 setNearbyLocations([]);
    //                 setIsLoading(false);
    //                 return;
    //             }

    //             // Get current location's nickname
    //             const nickname = getNickname();

    //             // Find current location
    //             let currentLocation = findCurrentLocation(data, nickname);

    //             // Editor fallback: use first location if no match
    //             if (!currentLocation) {
    //                 if (editorMode && data.length > 0) {
    //                     currentLocation = data[0];
    //                 } else {
    //                     setNearbyLocations([]);
    //                     setIsLoading(false);
    //                     return;
    //                 }
    //             }

    //             // Filter out current location
    //             const filtered = data.filter(
    //                 (loc) => loc.bidHash !== currentLocation!.bidHash
    //             );

    //             // Sort by squared distance from current location
    //             const currentLat = currentLocation.latitude;
    //             const currentLon = currentLocation.longitude;

    //             const sorted = filtered.sort((a, b) => {
    //                 const distA = squaredDistance(
    //                     currentLat,
    //                     currentLon,
    //                     a.location.coordinates[0],
    //                     a.location.coordinates[1]
    //                 );
    //                 const distB = squaredDistance(
    //                     currentLat,
    //                     currentLon,
    //                     b.location.coordinates[0],
    //                     b.location.coordinates[1]
    //                 );
    //                 return distA - distB;
    //             });

    //             // Return all nearby locations (no limit)
    //             setNearbyLocations(sorted);
    //             setIsLoading(false);
    //         },
    //         () => {
    //             setError("Failed to load nearby locations");
    //             setIsLoading(false);
    //         }
    //     );
    // }, [userContext, editorMode]);

    // Handle pre-fetched data or fetch on mount
    useEffect(() => {
        if (hasPreFetchedData) {
            setNearbyLocations(data as NearbyLocationData[]);
            setIsLoading(false);
        } else if (isStenciled) {
            setIsLoading(true);
        }
    }, [data, hasPreFetchedData, isStenciled]);

    // Loading state
    if (isLoading) {
        return <LoadingSkeleton />;
    }



    // // Error state
    // if (error) {
    //     return (
    //         <div className="v-nearby-error">
    //             <span>{error}</span>
    //             <button
    //                 onClick={() => {}}
    //                 className="wb-link-button"
    //                 type="button"
    //             >
    //                 Retry
    //             </button>
    //         </div>
    //     );
    // }

    // Empty state
    if (nearbyLocations.length === 0) {
        return (
            <NoRecordsBanner
                message={
                    editorMode
                        ? "Nearby locations will appear here at runtime."
                        : "No nearby locations found."
                }
                showInLive={true}
            />
        );
    }


    // Resolve linkPage ID to URL path using shared utility
    const pageURL = GetPageURLFromPageId(linkPage, userContext);

    // Render locations
    return (
        <div className="v-nearby-locations w-full">
            {nearbyLocations.map((location) => (
                <NearbyLocationCard
                    key={location.externalLocationId}
                    location={location}
                    showImage={showImage}
                    showDistance={showDistance}
                    userContext={userContext}
                    linkPage={pageURL}
                    linkTarget={linkTarget}
                    isEditorMode={editorMode}
                />
            ))}
        </div>
    );
}
